import { Box, Group, Text } from '@mantine/core';
import type { IgContextView, IgReferralView } from '../../../../lib/instagram-message';

/**
 * Context strips drawn above an Instagram message body — an ad-referral chip, a
 * quoted reply, a "replied to your story" header.
 *
 * Kept separate from the body renderers because any of them can accompany any
 * body kind; composing them here means each renderer stays about its own
 * content only.
 */

/** "From an Instagram ad" chip above a DM that originated from a
 *  Click-to-Instagram-Direct ad (or a referral link). */
export function ReferralChip({ referral }: { referral: IgReferralView }) {
  return (
    <Box
      style={{
        display: 'flex',
        gap: 8,
        alignItems: 'center',
        padding: '4px 8px',
        borderRadius: 8,
        background: 'light-dark(var(--mantine-color-grape-0), rgba(255,255,255,0.1))',
      }}
    >
      {referral.photoUrl && (
        <img
          src={referral.photoUrl}
          alt=""
          style={{ width: 28, height: 28, borderRadius: 4, objectFit: 'cover', flexShrink: 0 }}
        />
      )}
      <Box style={{ minWidth: 0 }}>
        <Text size="xs" fw={600} c="grape.7">
          {referral.source === 'ADS' ? 'From an Instagram ad' : 'Referral'}
        </Text>
        {referral.adTitle && (
          <Text size="xs" lineClamp={1}>
            {referral.adTitle}
          </Text>
        )}
      </Box>
    </Box>
  );
}

/** Quoted-reply strip above a message that replies to an earlier one. */
export function ReplyQuote({
  replyTo,
  incoming,
}: {
  replyTo: NonNullable<IgContextView['replyTo']>;
  incoming: boolean;
}) {
  return (
    <Box
      style={{
        borderLeft: `3px solid ${
          incoming ? 'var(--mantine-color-grape-5)' : 'rgba(255,255,255,0.6)'
        }`,
        paddingLeft: 6,
        opacity: 0.85,
      }}
    >
      <Text size="xs" fw={600}>
        {replyTo.isIncoming ? 'Them' : 'You'}
      </Text>
      <Text size="xs" lineClamp={2}>
        {replyTo.preview}
      </Text>
    </Box>
  );
}

/** "Replied to your story" context with a small thumbnail. The story media is
 *  on an expiring CDN, so the thumb may 404 — hide it if so. */
export function StoryReplyContext({ url, incoming }: { url?: string; incoming: boolean }) {
  return (
    <Group
      gap={6}
      wrap="nowrap"
      style={{
        borderLeft: `3px solid ${
          incoming ? 'var(--mantine-color-grape-5)' : 'rgba(255,255,255,0.6)'
        }`,
        paddingLeft: 6,
        opacity: 0.9,
      }}
    >
      {url && (
        <img
          src={url}
          alt=""
          style={{ width: 28, height: 40, borderRadius: 4, objectFit: 'cover', flexShrink: 0 }}
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
      )}
      <Text size="xs" fw={600}>
        Replied to your story
      </Text>
    </Group>
  );
}
